import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo, MouseEvent, useState } from 'react';
import { lt, useDialog } from '@mews/framework';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { RadioButton } from '@optimus-web/core';
import { Service } from '@mews-commander/data-access';
import { ProductsFormFields, ProductsOptionsNames } from '../../../../../schemas';
import { useAccountingCategoriesSuspenseQuery } from '../queries';
import { RadioButtonLabelWithCounter } from '../Conditions/RadioButtonLabelWithCounter';
import { useDeletedItems } from '../../../hooks';
import { AccountingCategoriesDialog } from './AccountingCategoriesDialog';

export const useAccountingCategoriesOption = ({
    service,
    parentFieldName,
    reflectedFieldName,
}: {
    service: Service;
    parentFieldName: string;
    reflectedFieldName: string;
}) => {
    const { deletedAccountingCategoriesCategory } = useDeletedItems(service.id);
    const deleteAccountingCategoriesCount = deletedAccountingCategoriesCategory?.options?.length ?? 0;
    const { data: accountingCategories } = useAccountingCategoriesSuspenseQuery();
    const [accountingCategoriesDialogProps, openAccountingCategoriesDialog] = useDialog();
    const { watch, setValue } = useFormContext();
    const accountingCategoriesFieldName = `${parentFieldName}.${ProductsFormFields.SelectedAccountingCategories}`;
    const selectedAccountingCategories = watch(accountingCategoriesFieldName);
    const selectedAccountingCategoriesCount = selectedAccountingCategories?.length ?? 0;
    const serviceAccountingCategories = useMemo(() => accountingCategories ?? [], [accountingCategories]);
    const totalAccountingCategoriesCount = serviceAccountingCategories?.length ?? deleteAccountingCategoriesCount;
    const accountingCategoriesOptions = useMemo(
        () =>
            serviceAccountingCategories.map((category) => ({
                label: category.name,
                id: category.id,
                value: category.id,
            })),
        [serviceAccountingCategories]
    );
    const [initialSelectedAccountingCategories, setInitialSelectedAccountingCategories] = useState<string[]>([]);
    const onAccountingCategoriesDialogOpen = (e: MouseEvent) => {
        e.preventDefault();
        setInitialSelectedAccountingCategories(selectedAccountingCategories);
        openAccountingCategoriesDialog();
    };

    useEffect(() => {
        if (selectedAccountingCategories == null && serviceAccountingCategories.length) {
            setValue(
                accountingCategoriesFieldName,
                serviceAccountingCategories.map(({ id }) => id)
            );
        }
    }, [accountingCategoriesFieldName, serviceAccountingCategories, setValue, selectedAccountingCategories]);

    return {
        accountingCategoriesOption: (
            <RadioButton
                label={
                    <RadioButtonLabelWithCounter
                        label={lt(TextKey.RoutingRulesFormSelectedAccountingCategoriesOption)}
                        isSelected={
                            watch(`${parentFieldName}.${ProductsFormFields.Type}`) ===
                            ProductsOptionsNames.SelectedAccountingCategories
                        }
                        onDialogOpen={onAccountingCategoriesDialogOpen}
                        selectedItemsCount={selectedAccountingCategoriesCount}
                        totalCount={totalAccountingCategoriesCount}
                        buttonTestId={DataTestId.RoutingRulesAccountingCategoriesEditButton}
                    />
                }
                value={ProductsOptionsNames.SelectedAccountingCategories}
                key={ProductsOptionsNames.SelectedAccountingCategories}
                disabled={totalAccountingCategoriesCount === 0}
            />
        ),
        accountingCategoriesDialog: totalAccountingCategoriesCount ? (
            <AccountingCategoriesDialog
                {...accountingCategoriesDialogProps}
                service={service}
                initialSelectedAccountingCategories={initialSelectedAccountingCategories}
                accountingCategoriesOptions={[
                    ...accountingCategoriesOptions,
                    ...(deletedAccountingCategoriesCategory != null ? [deletedAccountingCategoriesCategory] : []),
                ]}
                selectedAccountingCategoriesCount={selectedAccountingCategoriesCount}
                totalAccountingCategoriesCount={totalAccountingCategoriesCount}
                parentFieldName={parentFieldName}
                reflectedFieldName={reflectedFieldName}
            />
        ) : null,
    };
};
